import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2, FiPlus, FiX, FiFolder, FiCheckCircle, FiLoader, FiRefreshCw, FiUsers, FiMail, FiSmartphone, FiCalendar } from 'react-icons/fi';
import { createSector, updateSector, deleteSector, getSectors, getWhatsAppStatus, getWhatsAppQRCode, formatWhatsAppQrSrc, syncWhatsAppContacts, syncWhatsAppGroups, setSparkConnectionActive } from '../services/SectorService';
import {
  getCurrentSparkConnectionSession,
  getSparkConnectionAvailability,
  getSparkConnectionSession,
  sparkConnectionErrorMessage,
  startSparkConnectionSession,
  type SparkConnectionSession,
} from '../services/SparkConnectionSessionService';
import { getEmailConnectionStatus, fetchOAuthAuthorizeUrlAndRedirect, fetchMicrosoftOAuthAuthorizeUrlAndRedirect, disconnectEmail, getEmailDeliverabilityCheck, testSmtpConnection, saveSmtpConfig, type EmailConnectionStatus, type EmailDeliverabilityCheck } from '../services/EmailConnectionService';
import { disconnectCalendar, fetchCalendarAuthorizeUrlAndRedirect, getCalendarConnectionStatus, listGoogleCalendars, selectGoogleCalendar, type CalendarConnectionStatus, type GoogleCalendarChoice } from '../services/CalendarService';
import {
  completeMetaLogin,
  disconnectMeta,
  fetchMetaAuthorizeUrlAndRedirect,
  getMetaConnectionStatus,
  metaErrorMessage,
  type MetaAccountChannel,
  type MetaChannel,
  type MetaConnectionStatus,
  type MetaLoginResult,
  type WhatsAppCompleteResult,
} from '../services/MetaConnectionService';
import MetaAccountPicker from '../components/meta/MetaAccountPicker';
import MetaChannelsCard from '../components/meta/MetaChannelsCard';
import WebchatSiteCard from '../components/webchat/WebchatSiteCard';
import WhatsAppCloudCard, { isWhatsAppCloudConfigured } from '../components/meta/WhatsAppCloudCard';
import SessionService from '../services/SessionService';
import Toast from '../components/Toast';
import { ToastContainer } from '../components/Toast';
import { config } from '../config';
import './SectorsPage.css';
import { useSync } from '../contexts/SyncContext';

import { useModules } from '../contexts/ModuleContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ConfirmModal, SharedModal } from '../components/SharedModal';
import Switch from '../components/Switch/Switch';
import SparkMessageTemplatesPanel from '../components/broadcast/SparkMessageTemplatesPanel';
type SectorFiIconProps = { size?: number; className?: string };
const SectorIconX = FiX as React.ComponentType<SectorFiIconProps>;
const SectorIconCheckCircle = FiCheckCircle as React.ComponentType<SectorFiIconProps>;

function sparkMinutesLeft(expiresAt: string): number {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 1;
  return Math.max(1, Math.ceil(ms / 60000));
}

/** Estados do login da Meta já enviados: o React em desenvolvimento roda o efeito duas vezes e o state é de uso único. */
const META_LOGINS_IN_FLIGHT = new Set<string>();

/**
 * Troca do código da Meta ainda não aplicada na tela. Fica fora do componente: se a tela remontar enquanto a troca roda
 * (a URL já perdeu o code), a montagem nova aplica o resultado em vez de ele cair na montagem que já saiu.
 */
let pendingMetaLogin: Promise<MetaLoginResult> | null = null;

const ONBOARDING_RETURN_KEY = 'mends.onboarding.returnTo';

/**
 * O backend sempre devolve OAuth e Spark para /sectors. Se o onboarding (/comecar)
 * deixou um destino salvo, consome a chave e devolve o caminho com a query anexada.
 * Só aceita caminhos internos; sem chave (ou storage indisponível) retorna null.
 */
function takeOnboardingReturnPath(query: string): string | null {
  let returnTo: string | null = null;
  try {
    returnTo = window.localStorage.getItem(ONBOARDING_RETURN_KEY);
    if (returnTo !== null) window.localStorage.removeItem(ONBOARDING_RETURN_KEY);
  } catch {
    return null;
  }
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return null;
  if (!query) return returnTo;
  return `${returnTo}${returnTo.includes('?') ? '&' : '?'}${query}`;
}

interface Sector {
  id?: number;
  name: string;
  userBusinessId?: number;
  phoneNumberId: string;
  accessToken: string;
  description?: string;
  googleClientId?: string;
  googleApiKey?: string;
  isOfficial: boolean;
  sparkIsActive?: boolean | null;
  sparkLinePhone?: string | null;
  whatsappConnection?: {
    id: number;
    sectorId: number;
    status: string;
    qrcodeBase64: string | null;
    lastQrcodeGeneratedAt: string | null;
    lastConnectedAt: string | null;
    lastDisconnectedAt: string | null;
    sessionData: string | null;
    createdAt: string;
    updatedAt: string;
    last_contact_sync?: string;
    last_group_sync?: string;
  };
}

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface QRCodeData {
  qrcode: string;
  instructions?: string;
}

interface ContactSyncStatus {
  status: 'starting' | 'syncing' | 'completed' | 'error';
  currentCount: number;
  totalCount: number;
  contactName?: string;
  message?: string;
  percentage: number;
}

const SectorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { startSync, resetSync, syncState } = useSync();
  const { hasModule } = useModules();
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [editingSector, setEditingSector] = useState<Sector | null>(null);
  const [sectorData, setSectorData] = useState<Sector>({
    name: '',
    phoneNumberId: '',
    accessToken: '',
    description: '',
    isOfficial: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [isDeleting, setIsDeleting] = useState<{ [key: number]: boolean }>({});
  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [qrCodeData, setQRCodeData] = useState<QRCodeData | null>(null);
  const [whatsappStatus, setWhatsappStatus] = useState<{ status: string; message: string } | null>(null);
  const [selectedSectorForQR, setSelectedSectorForQR] = useState<number | null>(null);
  const [statusPollingInterval, setStatusPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [isLoadingQRCode, setIsLoadingQRCode] = useState<boolean>(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const qrPollRef = useRef<NodeJS.Timeout | null>(null);
  const whatsappStatusCheckedRef = useRef<string>('');

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isSyncingContacts, setIsSyncingContacts] = useState<{ [key: number]: boolean }>({});
  const [isSyncingGroups, setIsSyncingGroups] = useState<{ [key: number]: boolean }>({});
  const [syncMessage, setSyncMessage] = useState<{ [key: number]: string }>({});
  const [emailStatus, setEmailStatus] = useState<Record<number, EmailConnectionStatus | null>>({});
  const [calendarStatus, setCalendarStatus] = useState<Record<number, CalendarConnectionStatus | null>>({});
  const [calendarChoices, setCalendarChoices] = useState<Record<number, GoogleCalendarChoice[]>>({});
  const [connectingCalendarId, setConnectingCalendarId] = useState<number | null>(null);
  const [disconnectingCalendarId, setDisconnectingCalendarId] = useState<number | null>(null);
  const [confirmDisconnectCalendarId, setConfirmDisconnectCalendarId] = useState<number | null>(null);
  /** undefined = ainda consultando · null = a consulta falhou */
  const [metaStatus, setMetaStatus] = useState<Record<number, MetaConnectionStatus | null>>({});
  /** Setor e canal cujo login da Meta está abrindo. */
  const [connectingMeta, setConnectingMeta] = useState<{ sectorId: number; channel: MetaChannel } | null>(null);
  const [disconnectingMetaId, setDisconnectingMetaId] = useState<number | null>(null);
  const [confirmDisconnectMeta, setConfirmDisconnectMeta] = useState<{ sectorId: number; channel: MetaAccountChannel } | null>(null);
  // Lido da URL ao montar (e não só num efeito): se a tela remontar quando a conexão é escolhida, o seletor volta.
  const [metaPickerSectorId, setMetaPickerSectorId] = useState<number | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = parseInt(params.get('sectorId') || '', 10);
    return params.get('meta_select') && !isNaN(sid) ? sid : null;
  });
  const closeMetaPicker = useCallback(() => {
    setMetaPickerSectorId(null);
    if (new URLSearchParams(window.location.search).has('meta_select')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const [showSmtpModal, setShowSmtpModal] = useState(false);
  const [smtpSectorId, setSmtpSectorId] = useState<number | null>(null);
  const [smtpModalScreen, setSmtpModalScreen] = useState<'providers' | 'hostinger' | 'hostgator'>('providers');
  const [smtpManualHost, setSmtpManualHost] = useState('');
  const [smtpManualPort, setSmtpManualPort] = useState(587);
  const [smtpManualEmail, setSmtpManualEmail] = useState('');
  const [smtpManualPassword, setSmtpManualPassword] = useState('');
  const [smtpManualBusy, setSmtpManualBusy] = useState(false);
  const [confirmDisconnectSectorId, setConfirmDisconnectSectorId] = useState<number | null>(null);
  const [disconnectingSectorId, setDisconnectingSectorId] = useState<number | null>(null);
  const [confirmWhatsAppRelinkSectorId, setConfirmWhatsAppRelinkSectorId] = useState<number | null>(null);
  /** Sector id when deliverability modal is open; null = closed */
  const [deliverabilityModalSectorId, setDeliverabilityModalSectorId] = useState<number | null>(null);
  const [deliveryCheck, setDeliveryCheck] = useState<Record<number, EmailDeliverabilityCheck | null>>({});
  const [deliveryLoading, setDeliveryLoading] = useState<Record<number, boolean>>({});
  const [deliveryFetchError, setDeliveryFetchError] = useState<Record<number, string | null>>({});
  const [togglingSparkId, setTogglingSparkId] = useState<number | null>(null);
  const [sparkAvailable, setSparkAvailable] = useState(false);
  const [sparkModalOpen, setSparkModalOpen] = useState(false);
  const [sparkModalName, setSparkModalName] = useState('');
  const [sparkModalSectorId, setSparkModalSectorId] = useState<number | undefined>();
  const [sparkNameError, setSparkNameError] = useState('');
  const [sparkStarting, setSparkStarting] = useState(false);
  const [sparkChecking, setSparkChecking] = useState(false);
  const [sparkPending, setSparkPending] = useState<SparkConnectionSession | null>(null);
  const [sparkFailure, setSparkFailure] = useState<SparkConnectionSession | null>(null);
  const [templateSectorId, setTemplateSectorId] = useState<number | null>(null);
  const sparkPollRef = useRef<number | null>(null);

  useEffect(() => {
    void fetchSectors(true);

    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('oauth_success');
    const oauthError = params.get('oauth_error');
    const oauthSectorId = params.get('sectorId');
    const oauthProvider = params.get('oauth_provider');
    if (oauthSuccess && oauthSectorId) {
      const sid = parseInt(oauthSectorId, 10);
      if (oauthProvider === 'google_calendar') {
        if (!isNaN(sid)) {
          getCalendarConnectionStatus(sid).then(status =>
            setCalendarStatus(prev => ({ ...prev, [sid]: status }))
          ).catch(() => {});
        }
        addToast('Google Agenda conectada.', 'success');
      } else {
        if (!isNaN(sid)) {
          getEmailConnectionStatus(sid).then(status =>
            setEmailStatus(prev => ({ ...prev, [sid]: status }))
          ).catch(() => {});
        }
        const msg =
          oauthProvider === 'microsoft'
            ? 'Microsoft 365 / Outlook conectado.'
            : oauthProvider === 'gmail'
              ? 'Gmail conectado.'
              : 'E-mail conectado.';
        addToast(msg, 'success');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (oauthError) {
      addToast(`Erro na conexão: ${oauthError}`, 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Retorno do login da Meta: escolher as Páginas, ou mostrar por que não deu certo.
    const metaError = params.get('meta_error');
    // Volta da Meta com code e state: o POST autenticado confere se quem voltou é quem começou e só então troca o código.
    const metaCode = params.get('meta_code');
    const metaState = params.get('meta_state');
    if (metaCode && metaState) {
      window.history.replaceState({}, '', window.location.pathname);
      if (!META_LOGINS_IN_FLIGHT.has(metaState)) {
        META_LOGINS_IN_FLIGHT.add(metaState);
        pendingMetaLogin = completeMetaLogin(metaCode, metaState);
      }
    }
    let metaLoginUnmounted = false;
    const metaLogin = pendingMetaLogin;
    if (metaLogin) {
      // Só a montagem viva aplica o resultado, uma vez; se ela sair antes, a próxima montagem o aplica.
      metaLogin
        .then(({ sectorId, needsSelection }) => {
          if (metaLoginUnmounted || pendingMetaLogin !== metaLogin) return;
          pendingMetaLogin = null;
          if (needsSelection === false) {
            // Instagram: as contas foram escolhidas no diálogo da Meta, já conectou (sem seletor).
            window.history.replaceState({}, '', window.location.pathname);
            addToast('Instagram conectado.', 'success');
            void getMetaConnectionStatus(sectorId)
              .then(status => setMetaStatus(prev => ({ ...prev, [sectorId]: status })))
              .catch(() => undefined);
            return;
          }
          window.history.replaceState({}, '', `${window.location.pathname}?meta_select=1&sectorId=${sectorId}`);
          setMetaPickerSectorId(sectorId);
        })
        .catch((e: unknown) => {
          if (metaLoginUnmounted || pendingMetaLogin !== metaLogin) return;
          pendingMetaLogin = null;
          const data = (e as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
          addToast(data?.message?.trim() || metaErrorMessage(data?.code || 'exchange_failed'), 'error');
        });
    }
    if (metaError) {
      addToast(metaErrorMessage(metaError), 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if ((oauthSuccess && oauthSectorId) || oauthError) {
      const returnPath = takeOnboardingReturnPath(params.toString());
      if (returnPath) navigate(returnPath, { replace: true });
    }

    return () => {
      metaLoginUnmounted = true;
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
        setStatusPollingInterval(null);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const sparkSession = new URLSearchParams(window.location.search).get('sparkSession');

    const stopPoll = () => {
      if (sparkPollRef.current != null) {
        window.clearInterval(sparkPollRef.current);
        sparkPollRef.current = null;
      }
    };

    const finish = (session: SparkConnectionSession) => {
      stopPoll();
      setSparkChecking(false);
      window.history.replaceState({}, '', window.location.pathname);
      if (
        sparkSession &&
        (session.status === 'completed' || session.status === 'failed' || session.status === 'expired')
      ) {
        const returnPath = takeOnboardingReturnPath(
          new URLSearchParams({ sparkSession, sparkStatus: session.status }).toString()
        );
        if (returnPath) {
          if (session.status === 'completed') notifySectorsUpdated();
          navigate(returnPath, { replace: true });
          return;
        }
      }
      if (session.status === 'completed') {
        setSparkFailure(null);
        setSparkPending(null);
        addToast(
          session.displayPhone
            ? `WhatsApp conectado. Linha: ${session.displayPhone}`
            : 'WhatsApp conectado',
          'success'
        );
        void fetchSectors(true);
        notifySectorsUpdated();
        return;
      }
      setSparkPending(null);
      setSparkFailure({
        ...session,
        userMessage: session.userMessage || 'Não foi possível concluir a conexão. Comece de novo.',
      });
    };

    let settled = false;
    let deadline = Date.now() + 20 * 60 * 1000;
    const expiredSession = (id: string, name = '', sectorId?: number): SparkConnectionSession => ({
      sessionId: null,
      continuationId: id,
      status: 'expired',
      expiresAt: new Date(deadline).toISOString(),
      connectionName: name,
      sectorId: sectorId ?? null,
      displayPhone: null,
      reason: 'expired',
      userMessage: 'Este link de conexão expirou. Comece de novo.',
      url: null,
    });

    let inFlight = false;
    const tick = async (id: string) => {
      if (inFlight) return;
      inFlight = true;
      try {
        const session = await getSparkConnectionSession(id);
        if (cancelled || settled) return;
        const expiresAt = new Date(session.expiresAt).getTime();
        if (Number.isFinite(expiresAt)) deadline = expiresAt;
        const pastExpiry = Number.isFinite(expiresAt) && expiresAt <= Date.now();
        if (session.status === 'pending' && pastExpiry) {
          settled = true;
          finish({
            ...session,
            status: 'expired',
            userMessage: 'Este link de conexão expirou. Comece de novo.',
          });
          return;
        }
        if (session.status === 'linking' && pastExpiry) {
          deadline = Date.now() + 30_000;
        }
        if ((session.status === 'pending' || session.status === 'linking') && Date.now() <= deadline) {
          setSparkChecking(true);
          setSparkPending(session);
          return;
        }
        if (session.status === 'pending' || session.status === 'linking') {
          settled = true;
          finish(expiredSession(id, session.connectionName, session.sectorId ?? undefined));
          return;
        }
        settled = true;
        finish(session);
      } catch {
        if (cancelled || settled) return;
        if (Date.now() > deadline) {
          settled = true;
          finish(expiredSession(id));
        }
      } finally {
        inFlight = false;
      }
    };

    void (async () => {
      try {
        const available = await getSparkConnectionAvailability();
        if (!cancelled) setSparkAvailable(available);
      } catch {
        if (!cancelled) setSparkAvailable(false);
      }

      if (sparkSession) {
        setSparkChecking(true);
        await tick(sparkSession);
        if (cancelled || settled) return;
        sparkPollRef.current = window.setInterval(() => {
          void tick(sparkSession);
        }, 3000);
        return;
      }

      try {
        const current = await getCurrentSparkConnectionSession();
        if (!cancelled && current && (current.status === 'pending' || current.status === 'linking')) {
          setSparkPending(current);
        }
      } catch {
        // The resume banner is optional.
      }
    })();

    return () => {
      cancelled = true;
      stopPoll();
    };
  }, []);

  const unofficialSectorIdsKey = useMemo(
    () => sectors
      .filter(s => s.id && !s.isOfficial)
      .map(s => s.id!)
      .sort((a, b) => a - b)
      .join(','),
    [sectors]
  );

  useEffect(() => {
    if (!unofficialSectorIdsKey || whatsappStatusCheckedRef.current === unofficialSectorIdsKey) return;
    whatsappStatusCheckedRef.current = unofficialSectorIdsKey;

    const unofficialSectorIds = unofficialSectorIdsKey.split(',').map(id => parseInt(id, 10));
    if (unofficialSectorIds.length === 0) return;

    let cancelled = false;

    const checkUnofficialSectorsStatus = async () => {
      const results = await Promise.all(
        unofficialSectorIds.map(async (sectorId) => {
          try {
            const status = await getWhatsAppStatus(sectorId, false);
            return { sectorId, status, error: false as const };
          } catch (error) {
            console.error(`Erro ao verificar status do setor ${sectorId}: `, error);
            return { sectorId, status: null, error: true as const };
          }
        })
      );

      if (cancelled) return;

      const statusById = new Map(
        results.filter(r => !r.error && r.status).map(r => [r.sectorId, r.status!])
      );
      if (statusById.size === 0) return;

      setSectors(prev => prev.map(s => {
        if (!s.id) return s;
        const status = statusById.get(s.id);
        if (!status) return s;
        return {
          ...s,
          whatsappConnection: s.whatsappConnection
            ? {
              ...s.whatsappConnection,
              status: status.status,
              last_contact_sync: status.last_contact_sync,
              last_group_sync: status.last_group_sync
            }
            : {
              id: 0,
              sectorId: s.id,
              status: status.status,
              qrcodeBase64: null,
              lastQrcodeGeneratedAt: null,
              lastConnectedAt: null,
              lastDisconnectedAt: null,
              sessionData: null,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              last_contact_sync: status.last_contact_sync,
              last_group_sync: status.last_group_sync
            }
        };
      }));
    };

    void checkUnofficialSectorsStatus();
    return () => { cancelled = true; };
  }, [unofficialSectorIdsKey]);

  useEffect(() => {
    let statusInterval: NodeJS.Timeout | null = null;
    let ws: WebSocket | null = null;

    const stopStatusPolling = () => {
      if (statusInterval) {
        clearInterval(statusInterval);
        statusInterval = null;
      }
      if (statusPollingInterval) {
        clearInterval(statusPollingInterval);
        setStatusPollingInterval(null);
      }
    };

    const closeWebSocket = () => {
      if (ws) {
        ws.close();
        ws = null;
      }
    };

    if (showQRCode && selectedSectorForQR) {
      const sector = sectors.find(s => s.id === selectedSectorForQR);
      if (sector && !sector.isOfficial) {
        const wsUrl = config.WS_BASE_URL || 'wss://server.mends.pro';
        const token = SessionService.getToken() || '';
        if (token) {
          const fullWsUrl = `${wsUrl}/api/v1/ws?sector_id=${selectedSectorForQR}&token=${encodeURIComponent(token)}`;
          ws = new WebSocket(fullWsUrl);
          ws.onopen = () => {};
          ws.onclose = () => {};
          ws.onerror = () => {};
          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data?.type === 'ping') {
                ws?.send(JSON.stringify({ type: 'pong' }));
              }
            } catch {
              // ignore non-json frames
            }
          };
        }

        const checkStatus = async () => {
          try {
            const status = await getWhatsAppStatus(selectedSectorForQR, false);
            setWhatsappStatus(status);

            if (status.status === 'connected') {
              setSectors(prev => prev.map(s =>
                s.id === selectedSectorForQR
                  ? {
                    ...s,
                    whatsappConnection: s.whatsappConnection
                      ? { ...s.whatsappConnection, status: status.status }
                      : {
                        id: 0,
                        sectorId: s.id!,
                        status: status.status,
                        qrcodeBase64: null,
                        lastQrcodeGeneratedAt: null,
                        lastConnectedAt: new Date().toISOString(),
                        lastDisconnectedAt: null,
                        sessionData: null,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                      }
                  }
                  : s
              ));
              stopStatusPolling();
              if (qrPollRef.current) {
                clearInterval(qrPollRef.current);
                qrPollRef.current = null;
              }
              addToast('WhatsApp conectado.', 'success');
              setTimeout(() => handleCloseQRCode(), 1800);
            }
          } catch (error) {
            console.error('Erro ao verificar status:', error);
          }
        };

        void checkStatus();
        statusInterval = setInterval(checkStatus, 2500);
        setStatusPollingInterval(statusInterval);
      }
    }

    return () => {
      stopStatusPolling();
      closeWebSocket();
    };
  }, [showQRCode, selectedSectorForQR]);

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(current => [...current, { id, message, type }]);
    setTimeout(() => removeToast(id), 3000);
  };

  const removeToast = (id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  };

  const fetchEmailStatuses = useCallback(async (sectorIds: number[]) => {
    if (sectorIds.length === 0) return;
    const results = await Promise.all(
      sectorIds.map(async (sid) => {
        try {
          const status = await getEmailConnectionStatus(sid);
          return { sid, status };
        } catch {
          return { sid, status: null };
        }
      })
    );
    setEmailStatus(prev => {
      const next = { ...prev };
      for (const { sid, status } of results) {
        next[sid] = status;
      }
      return next;
    });
  }, []);

  const fetchCalendarStatuses = useCallback(async (sectorIds: number[]) => {
    if (sectorIds.length === 0) return;
    const results = await Promise.all(
      sectorIds.map(async (sid) => {
        try {
          const status = await getCalendarConnectionStatus(sid);
          return { sid, status };
        } catch {
          return { sid, status: null };
        }
      })
    );
    setCalendarStatus(prev => {
      const next = { ...prev };
      for (const { sid, status } of results) {
        next[sid] = status;
      }
      return next;
    });
  }, []);

  const fetchMetaStatuses = useCallback(async (sectorIds: number[]) => {
    if (sectorIds.length === 0) return;
    const results = await Promise.all(
      sectorIds.map(async (sid) => {
        try {
          return { sid, status: await getMetaConnectionStatus(sid) };
        } catch {
          return { sid, status: null };
        }
      })
    );
    setMetaStatus(prev => {
      const next = { ...prev };
      for (const { sid, status } of results) next[sid] = status;
      return next;
    });
  }, []);

  const fetchDeliverability = useCallback(async (sectorId: number) => {
    setDeliveryLoading(prev => ({ ...prev, [sectorId]: true }));
    setDeliveryFetchError(prev => ({ ...prev, [sectorId]: null }));
    try {
      const data = await getEmailDeliverabilityCheck(sectorId);
      setDeliveryCheck(prev => ({ ...prev, [sectorId]: data }));
    } catch (e: unknown) {
      let msg = 'Não foi possível verificar o DNS. Tente de novo.';
      if (e && typeof e === 'object' && 'response' in e) {
        const r = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
        if (r) msg = r;
      } else if (e instanceof Error) msg = e.message;
      setDeliveryFetchError(prev => ({ ...prev, [sectorId]: msg }));
      setDeliveryCheck(prev => ({ ...prev, [sectorId]: null }));
    } finally {
      setDeliveryLoading(prev => ({ ...prev, [sectorId]: false }));
    }
  }, []);

  const fetchSectors = useCallback(async (forceRefresh = false) => {
    try {
      const token = SessionService.getToken();
      if (!token) {
        navigate('/');
        return;
      }
      const response = await getSectors(token, forceRefresh);
      const list = response || [];
      setSectors(list);
      whatsappStatusCheckedRef.current = '';
      if (list.length > 0) {
        const ids = list.map(s => s.id!).filter((id): id is number => id != null);
        void fetchEmailStatuses(ids);
        void fetchCalendarStatuses(ids);
        void fetchMetaStatuses(ids);
      }
    } catch (error) {
      console.error('Falha ao buscar setores', error);
      setSectors([]);
    } finally {
      setIsInitialLoading(false);
    }
  }, [navigate, fetchEmailStatuses, fetchCalendarStatuses, fetchMetaStatuses]);

  const notifySectorsUpdated = useCallback(() => {
    window.dispatchEvent(new CustomEvent('sectorsUpdated'));
  }, []);

  /** O Embedded Signup terminou e o servidor ligou o setor: atualiza o status e recarrega (is_official e o Spark mudaram). */
  const handleWhatsAppConnected = (sectorId: number, result: WhatsAppCompleteResult) => {
    setMetaStatus(prev => ({ ...prev, [sectorId]: result.status }));
    addToast(
      result.isOnBizApp
        ? 'WhatsApp oficial conectado. As conversas dos últimos 6 meses estão sendo importadas.'
        : 'WhatsApp oficial conectado.',
      'success',
    );
    if (result.sparkSwitchedOff) addToast('A conexão anterior do WhatsApp deste setor foi desligada: tudo passa a ir direto pela Meta.', 'info');
    void fetchSectors(true);
    notifySectorsUpdated();
  };

  const openSparkConnect = (name = '', sectorId?: number) => {
    setSparkModalName(name);
    setSparkModalSectorId(sectorId);
    setSparkNameError('');
    setSparkModalOpen(true);
  };

  const handleStartSparkConnect = async () => {
    const name = sparkModalName.trim();
    if (!name) {
      setSparkNameError('Informe o nome da conexão');
      return;
    }
    setSparkStarting(true);
    setSparkFailure(null);
    try {
      const session = await startSparkConnectionSession({
        connectionName: name,
        sectorId: sparkModalSectorId,
      });
      if (!session.url) {
        throw new Error('Não foi possível iniciar a conexão. Comece de novo.');
      }
      window.location.assign(session.url);
    } catch (error) {
      setSparkStarting(false);
      addToast(sparkConnectionErrorMessage(error, 'Não foi possível iniciar a conexão. Comece de novo.'), 'error');
    }
  };

  const handleToggleSparkActive = async (sector: Sector, next: boolean) => {
    if (!sector.id) return;
    setTogglingSparkId(sector.id);
    try {
      const updated = await setSparkConnectionActive(sector.id, next);
      setSectors(prev => prev.map(s =>
        s.id === sector.id
          ? {
              ...s,
              sparkIsActive: updated.sparkIsActive ?? next,
              sparkLinePhone: updated.sparkLinePhone ?? s.sparkLinePhone,
              whatsappConnection: s.whatsappConnection
                ? { ...s.whatsappConnection, status: next ? 'connected' : 'disconnected' }
                : s.whatsappConnection
            }
          : s
      ));
      notifySectorsUpdated();
      addToast(next ? 'WhatsApp oficial ativado' : 'WhatsApp oficial desativado', 'success');
    } catch (error) {
      console.error(error);
      addToast('Não foi possível atualizar a conexão do WhatsApp oficial', 'error');
    } finally {
      setTogglingSparkId(null);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string | undefined } = {};

    if (!sectorData.name) {
      newErrors.name = 'Informe o nome da conexão';
    }
    if (!sectorData.description) {
      newErrors.description = 'Informe a descrição';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const token = SessionService.getToken();
      if (!token) {
        addToast('Erro de autenticação', 'error');
        return;
      }
      const decodedToken = SessionService.decodeToken(token);
      const userBusinessId = decodedToken?.userId;
      if (!userBusinessId) {
        addToast('Erro de autenticação', 'error');
        return;
      }
      const updatedSectorData = {
        name: sectorData.name,
        description: sectorData.description || '',
        isOfficial: editingSector?.isOfficial ?? false,
        phoneNumberId: editingSector?.phoneNumberId ?? '',
        accessToken: editingSector?.accessToken ?? '',
        userBusinessId
      };
      if (editingSector?.id) {
        try {
          const response = await updateSector(editingSector.id, updatedSectorData);
          if (response) {
            const updated = response?.data;
            if (updated?.id) {
              setSectors(prev => prev.map(s => (s.id === updated.id ? { ...s, ...updated } : s)));
            }
            await fetchSectors(true);
            addToast('Conexão atualizada', 'success');
            handleCloseDrawer();
            notifySectorsUpdated();
          }
        } catch (error: any) {
          console.error('Erro ao atualizar setor:', error);
          const errorMessage = error?.response?.data?.message;
          addToast(errorMessage || 'Erro ao atualizar a conexão', 'error');
        }
      } else {
        try {
          const response = await createSector(updatedSectorData);
          if (response) {
            const created = response?.data;
            let list: typeof sectors = [];
            try {
              list = await getSectors(token, true);
              setSectors(list);
            } catch (listErr) {
              console.warn('Connection created but list refresh failed:', listErr);
              if (created?.id) {
                setSectors(prev => (prev.some(s => s.id === created.id) ? prev : [...prev, created]));
              }
            }
            whatsappStatusCheckedRef.current = '';
            if (created?.id && list.length > 0 && !list.some(s => s.id === created.id)) {
              addToast(
                'A conexão foi criada, mas não está vinculada à sua conta. Atualize a página ou fale com o suporte.',
                'error'
              );
            } else {
              addToast('Conexão criada', 'success');
              handleCloseDrawer();
              notifySectorsUpdated();
            }
          }
        } catch (error: any) {
          console.error('Erro ao criar setor:', error);
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.Message;
          addToast(errorMessage || 'Erro ao criar a conexão', 'error');
        }
      }
    } catch (error) {
      console.error('Erro geral:', error);
      addToast('Erro ao processar a operação', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setIsDeleting(prev => ({ ...prev, [id]: true }));
    try {
      await deleteSector(id);
      setSectors(prev => prev.filter(sector => sector.id !== id));
      setEmailStatus(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      addToast('Conexão excluída', 'success');
      notifySectorsUpdated();
    } catch (error) {
      console.error('Erro ao excluir setor:', error);
      addToast('Erro ao excluir a conexão', 'error');
    } finally {
      setIsDeleting(prev => ({ ...prev, [id]: false }));
      setConfirmDeleteId(null);
    }
  };

  // Cleanup drawer-open on unmount (prevents app freeze if user navigates away with drawer open)
  useEffect(() => {
    return () => {
      document.body.classList.remove('drawer-open');
    };
  }, []);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingSector(null);
    setSectorData({
      name: '',
      phoneNumberId: '',
      accessToken: '',
      description: '',
      isOfficial: false,
    });
    setErrors({});
    document.body.classList.remove('drawer-open');
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
    document.body.classList.add('drawer-open');
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCloseDrawer();
    }
  };

  const handleCloseSmtpModal = () => {
    setShowSmtpModal(false);
    setSmtpSectorId(null);
    setSmtpModalScreen('providers');
    setSmtpManualHost('');
    setSmtpManualPort(587);
    setSmtpManualEmail('');
    setSmtpManualPassword('');
    setSmtpManualBusy(false);
  };

  // Função para buscar QR code
  const fetchQRCode = useCallback(async (sectorId: number, relink: boolean, silent = false) => {
    try {
      if (!silent) {
        setIsLoadingQRCode(true);
        setQrError(null);
      }
      const sector = sectors.find(s => s.id === sectorId);
      if (!sector) return;

      const response = await getWhatsAppQRCode(sectorId, sector.isOfficial, relink);
      setQRCodeData(response);
      if (!response.qrcode?.trim()) {
        const providerMsg = response.message?.trim();
        const isWhatsAppRateLimit = providerMsg
          ? /conectar novos dispositivos|connect new devices|try again later/i.test(providerMsg)
          : false;
        setQrError(
          providerMsg
            ? (isWhatsAppRateLimit
              ? `${providerMsg} Aguarde de 30 a 60 minutos, remova do celular os aparelhos conectados que você não usa e tente de novo sem clicar em Atualizar várias vezes.`
              : providerMsg)
            : 'O QR code está sendo gerado. Aguarde alguns segundos…'
        );
      } else {
        setQrError(null);
      }
    } catch (error) {
      console.error('Erro ao buscar QR code:', error);
      setQrError('Não foi possível carregar o QR code.');
      if (!silent) addToast('Erro ao gerar o QR code', 'error');
    } finally {
      if (!silent) setIsLoadingQRCode(false);
    }
  }, [sectors]);

  const proceedShowQRCode = (sectorId: number, relink: boolean) => {
    setWhatsappStatus(null);
    setQRCodeData(null);
    setQrError(null);
    setSelectedSectorForQR(sectorId);
    setShowQRCode(true);
    setConfirmWhatsAppRelinkSectorId(null);
    void fetchQRCode(sectorId, relink);

    if (qrPollRef.current) clearInterval(qrPollRef.current);
    qrPollRef.current = setInterval(() => {
      void fetchQRCode(sectorId, false, true);
    }, 20000);
  };

  const requestWhatsAppQR = (sector: Sector) => {
    if (!sector.id) return;
    const st = (sector.whatsappConnection?.status || '').toLowerCase();
    const relink = st === 'connected' || st === 'connecting';
    if (relink) {
      setConfirmWhatsAppRelinkSectorId(sector.id);
      return;
    }
    proceedShowQRCode(sector.id, false);
  };

  // Função para fechar QR code
  const handleCloseQRCode = () => {
    setShowQRCode(false);
    setQRCodeData(null);
    setQrError(null);
    setWhatsappStatus(null);
    setSelectedSectorForQR(null);
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
      setStatusPollingInterval(null);
    }
    if (qrPollRef.current) {
      clearInterval(qrPollRef.current);
      qrPollRef.current = null;
    }
  };

  // Função para sincronizar contatos manualmente
  const handleSyncContacts = async (sectorId: number) => {
    try {
      setIsSyncingContacts(prev => ({ ...prev, [sectorId]: true }));
      setSyncMessage(prev => ({ ...prev, [sectorId]: 'Sincronizando contatos...' }));
      addToast('Iniciando a sincronização de contatos...', 'info');
      startSync(sectorId, 'contacts');

      await syncWhatsAppContacts(sectorId);
      addToast('Sincronização de contatos em andamento. Acompanhe na barra de progresso abaixo.', 'info');
    } catch (error: any) {
      console.error('Error syncing contacts:', error);
      setIsSyncingContacts(prev => ({ ...prev, [sectorId]: false }));
      setSyncMessage(prev => ({ ...prev, [sectorId]: '' }));
      resetSync();

      if (error.response?.status === 429) {
        const message = error.response.data?.error || 'Muitas tentativas. Aguarde um pouco.';
        addToast(message, 'info');
      } else if (error.response?.status === 409) {
        addToast(error.response.data?.error || 'Já existe uma sincronização em andamento.', 'info');
      } else {
        addToast(error.response?.data?.message || error.response?.data?.error || 'Erro ao iniciar a sincronização de contatos', 'error');
      }
    }
  };

  // Função para sincronizar grupos manualmente
  const handleSyncGroups = async (sectorId: number) => {
    try {
      setIsSyncingGroups(prev => ({ ...prev, [sectorId]: true }));
      setSyncMessage(prev => ({ ...prev, [sectorId]: 'Sincronizando grupos...' }));
      addToast('Iniciando a sincronização de grupos...', 'info');
      startSync(sectorId, 'groups');

      await syncWhatsAppGroups(sectorId);
      addToast('Sincronização de grupos em andamento. Acompanhe na barra de progresso abaixo.', 'info');
    } catch (error: any) {
      console.error('Error syncing groups:', error);
      setIsSyncingGroups(prev => ({ ...prev, [sectorId]: false }));
      setSyncMessage(prev => ({ ...prev, [sectorId]: '' }));
      resetSync();

      if (error.response?.status === 429) {
        const message = error.response.data?.error || 'Muitas tentativas. Aguarde um pouco.';
        addToast(message, 'info');
      } else if (error.response?.status === 409) {
        addToast(error.response.data?.error || 'Já existe uma sincronização em andamento.', 'info');
      } else {
        addToast(error.response?.data?.message || error.response?.data?.error || 'Erro ao iniciar a sincronização de grupos', 'error');
      }
    }
  };


  const CONTACT_SYNC_COOLDOWN_MS = 5 * 60 * 1000;
  const GROUP_SYNC_COOLDOWN_MS = 10 * 60 * 1000;

  const getSyncRemainingTime = (lastSync?: string, cooldownMs: number = CONTACT_SYNC_COOLDOWN_MS): string | null => {
    if (!lastSync) return null;
    const last = new Date(lastSync).getTime();
    if (Number.isNaN(last)) return null;
    const remainingMs = cooldownMs - (Date.now() - last);
    if (remainingMs <= 0) return null;
    const mins = Math.floor(remainingMs / 60000);
    const secs = Math.ceil((remainingMs % 60000) / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  useEffect(() => {
    const sectorId = syncState.sectorId;
    if (!sectorId) return;
    if (syncState.stage === 'completed') {
      setIsSyncingContacts(prev => ({ ...prev, [sectorId]: false }));
      setIsSyncingGroups(prev => ({ ...prev, [sectorId]: false }));
      setSyncMessage(prev => ({
        ...prev,
        [sectorId]: syncState.contactProgress.total || syncState.groupProgress.total
          ? `Concluído: ${syncState.contactProgress.current || syncState.groupProgress.current} processados`
          : 'Sincronização concluída',
      }));
      addToast(
        syncState.contactProgress.total || syncState.groupProgress.total
          ? `Sincronização concluída. Abra o Chat e selecione esta conexão para ver as conversas importadas.`
          : 'Sincronização concluída. Abra o Chat e selecione esta conexão.',
        'success'
      );
      void fetchSectors(true);
      const t = window.setTimeout(() => {
        setSyncMessage(prev => ({ ...prev, [sectorId]: '' }));
        resetSync();
      }, 4000);
      return () => window.clearTimeout(t);
    }
    if (syncState.stage === 'error') {
      setIsSyncingContacts(prev => ({ ...prev, [sectorId]: false }));
      setIsSyncingGroups(prev => ({ ...prev, [sectorId]: false }));
      setSyncMessage(prev => ({ ...prev, [sectorId]: syncState.errorMessage || 'A sincronização falhou' }));
      if (syncState.errorMessage) addToast(syncState.errorMessage, 'error');
    }
  }, [syncState.stage, syncState.sectorId, syncState.errorMessage, syncState.contactProgress, syncState.groupProgress, resetSync, fetchSectors]);

  /** A Cloud API direta da Meta tem conta ativa neste setor? */
  const cloudAccountOf = (sector: Sector) =>
    metaStatus[sector.id!]?.accounts.find(a => a.channel === 'whatsapp') ?? null;

  const renderWhatsAppStatus = (sector: Sector) => {
    if (sector.isOfficial) {
      const on = sector.sparkIsActive === true;
      const statusColor = on ? 'var(--success-color)' : 'var(--error-color)';
      return (
        <div className="whatsapp-status" style={{ color: statusColor }}>
          <span className="status-dot" style={{ backgroundColor: statusColor }} />
          <span>{on ? 'Conectado' : 'Desconectado'}</span>
        </div>
      );
    }

    if (!sector.whatsappConnection) return null;

    const raw = (sector.whatsappConnection.status || '').toLowerCase();
    let statusText: string;
    let statusColor: string;
    if (raw === 'connected') {
      statusText = 'Conectado';
      statusColor = 'var(--success-color)';
    } else if (raw === 'connecting') {
      statusText = 'Conectando';
      statusColor = 'var(--warning-color)';
    } else {
      statusText = 'Desconectado';
      statusColor = 'var(--error-color)';
    }

    return (
      <div className="whatsapp-status" style={{ color: statusColor }}>
        <span className="status-dot" style={{ backgroundColor: statusColor }} />
        <span>{statusText}</span>
      </div>
    );
  };

  const handleApiTypeChange = (isOfficial: boolean) => {
    if (isOfficial) {
      setSectorData(prev => ({
        ...prev,
        isOfficial: true,
        phoneNumberId: editingSector?.phoneNumberId || '',
        accessToken: editingSector?.accessToken || ''
      }));
    } else {
      setSectorData(prev => ({
        ...prev,
        isOfficial: false,
        phoneNumberId: '',
        accessToken: ''
      }));
    }
    setErrors({});
  };

  // Atualizar o formulário para incluir isOfficial
  const renderForm = () => (
    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
      <div className="form-group">
        <label>Nome da conexão <span className="required">*</span></label>
        <input
          type="text"
          className={`form-input ${errors.name ? 'error' : ''}`}
          value={sectorData.name}
          placeholder="Ex.: WhatsApp Vendas, WhatsApp Suporte..."
          onChange={(e) => {
            setSectorData({ ...sectorData, name: e.target.value });
            if (errors.name) setErrors({ ...errors, name: '' });
          }}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group">
        <label>Descrição <span className="required">*</span></label>
        <textarea
          className={`form-input form-textarea ${errors.description ? 'error' : ''}`}
          value={sectorData.description}
          placeholder="Descreva o objetivo ou a função desta conexão..."
          onChange={(e) => {
            setSectorData({ ...sectorData, description: e.target.value });
            if (errors.description) setErrors({ ...errors, description: '' });
          }}
        />
        {errors.description && <span className="error-message">{errors.description}</span>}
      </div>

      <div className="drawer-footer">
        <button type="button" className="btn btn-secondary" onClick={handleCloseDrawer} disabled={isSaving}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSaving}>
          {isSaving && (
            <FiLoader className="spin" style={{ marginRight: 8 }} />
          )}
          {editingSector ? 'Salvar' : 'Criar'}
        </button>
      </div>
    </form>
  );

  // Atualizar o card do setor para incluir status e botão de QR code
  const renderSectorCard = (sector: Sector) => {
    return (
      <div key={sector.id} className="sector-card">
        <div className="sector-card-header">
          <h3 title={sector.name}>{sector.name}</h3>
          <div className="sector-actions">
            <button
              className="action-button edit"
              aria-label={`Editar ${sector.name}`}
              onClick={() => {
                setEditingSector(sector);
                setSectorData({
                  name: sector.name,
                  description: sector.description,
                  isOfficial: sector.isOfficial,
                  phoneNumberId: sector.phoneNumberId || '',
                  accessToken: sector.accessToken || '',
                });
                handleOpenDrawer();
              }}
            >
              <FiEdit2 />
            </button>
            <button
              className="action-button delete"
              aria-label={`Excluir ${sector.name}`}
              onClick={() => setConfirmDeleteId(sector.id!)}
            >
              <FiTrash2 />
            </button>
          </div>
        </div>

        <div className="sector-card-content">
          {sector.description ? <p className="sector-description">{sector.description}</p> : null}

          {sector.isOfficial ? (
            // O cartão do Spark só aparece enquanto o setor não migrou: com a conta da Meta, o Spark sai da tela e o
            // WhatsAppCloudCard (abaixo) assume. Sem o WhatsApp oficial configurado no ambiente, nada muda.
            (() => {
              const hasSpark = sector.sparkIsActive !== undefined && sector.sparkIsActive !== null;
              const ws = metaStatus[sector.id!];
              // O status da Meta ainda não chegou: não mostra o cartão do Spark por um instante antes de trocar pelo do WhatsApp oficial.
              if (ws === undefined)
                return (
                  <div className="sector-channel">
                    <div className="sector-channel-head">
                      <h4>WhatsApp</h4>
                    </div>
                    <p className="sector-description">Verificando o WhatsApp…</p>
                  </div>
                );
              const cloudReady = isWhatsAppCloudConfigured(ws);
              if (cloudAccountOf(sector) || (!hasSpark && cloudReady)) return null;
              return (
            <div className="sector-channel">
              <div className="sector-channel-head">
                <h4>WhatsApp</h4>
              </div>
              {!hasSpark ? (
                sparkAvailable ? (
                  <button
                    type="button"
                    className="whatsapp-connect-button"
                    onClick={() => openSparkConnect(sector.name, sector.id)}
                  >
                    Conectar número
                  </button>
                ) : (
                  <p className="sector-description">O WhatsApp não está disponível no momento.</p>
                )
              ) : (
                <>
                  <Switch
                    checked={sector.sparkIsActive === true}
                    disabled={togglingSparkId === sector.id}
                    label="Ativa"
                    onChange={(next) => void handleToggleSparkActive(sector, next)}
                  />
                  {sector.sparkLinePhone ? (
                    <p className="sector-description">Linha {sector.sparkLinePhone}</p>
                  ) : null}
                  {sector.id ? (
                    <button
                      type="button"
                      className="whatsapp-connect-button is-quiet"
                      onClick={() => setTemplateSectorId(sector.id!)}
                    >
                      Modelos
                    </button>
                  ) : null}
                </>
              )}
            </div>
              );
            })()
          ) : (
            <div className="sector-channel">
              <div className="sector-channel-head">
                <h4>WhatsApp</h4>
                {renderWhatsAppStatus(sector)}
              </div>
              <div className="whatsapp-buttons-container">
                <button
                  className="whatsapp-connect-button"
                  onClick={() => requestWhatsAppQR(sector)}
                >
                  {(() => {
                    const st = (sector.whatsappConnection?.status || '').toLowerCase();
                    return st === 'connected' || st === 'connecting' ? 'Reconectar' : 'Conectar';
                  })()}
                </button>
                {(sector.whatsappConnection?.status || '').toLowerCase() === 'connected' && (
                  <>
                    {(() => {
                      const contactSyncRemaining = getSyncRemainingTime(sector.whatsappConnection?.last_contact_sync);
                      const groupSyncRemaining = getSyncRemainingTime(sector.whatsappConnection?.last_group_sync, GROUP_SYNC_COOLDOWN_MS);

                      return (
                        <>
                          <button
                            type="button"
                            className="whatsapp-connect-button is-quiet"
                            onClick={() => sector.id && handleSyncContacts(sector.id)}
                            title={contactSyncRemaining ? `Disponível em ${contactSyncRemaining}` : 'Sincronizar contatos'}
                            aria-label={contactSyncRemaining ? `Sincronizar contatos disponível em ${contactSyncRemaining}` : 'Sincronizar contatos'}
                            disabled={isSyncingContacts[sector.id!] || isSyncingGroups[sector.id!] || !!contactSyncRemaining}
                          >
                            {isSyncingContacts[sector.id!] ? <FiLoader className="spin" /> : <FiUsers />}
                            {contactSyncRemaining ? `Contatos ${contactSyncRemaining}` : 'Contatos'}
                          </button>
                          <button
                            type="button"
                            className="whatsapp-connect-button is-quiet"
                            onClick={() => sector.id && handleSyncGroups(sector.id)}
                            title={groupSyncRemaining ? `Disponível em ${groupSyncRemaining}` : 'Sincronizar grupos'}
                            aria-label={groupSyncRemaining ? `Sincronizar grupos disponível em ${groupSyncRemaining}` : 'Sincronizar grupos'}
                            disabled={isSyncingContacts[sector.id!] || isSyncingGroups[sector.id!] || !!groupSyncRemaining}
                          >
                            {isSyncingGroups[sector.id!] ? <FiLoader className="spin" /> : <FiFolder />}
                            {groupSyncRemaining ? `Grupos ${groupSyncRemaining}` : 'Grupos'}
                          </button>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
              {(() => {
                const isThisSectorSync =
                  syncState.sectorId === sector.id &&
                  (syncState.stage === 'contacts' ||
                    syncState.stage === 'groups' ||
                    syncState.stage === 'completed' ||
                    syncState.stage === 'error');
                const progress =
                  syncState.stage === 'groups' || syncState.groupProgress.total > 0
                    ? syncState.groupProgress
                    : syncState.contactProgress;
                const pct =
                  progress.total > 0
                    ? Math.min(100, Math.round((progress.current / progress.total) * 100))
                    : syncState.stage === 'completed'
                      ? 100
                      : isThisSectorSync
                        ? 8
                        : 0;
                const running =
                  isSyncingContacts[sector.id!] ||
                  isSyncingGroups[sector.id!] ||
                  (isThisSectorSync &&
                    (syncState.stage === 'contacts' || syncState.stage === 'groups'));
                const label =
                  syncMessage[sector.id!] ||
                  (isThisSectorSync
                    ? syncState.stage === 'error'
                      ? syncState.errorMessage || 'A sincronização falhou'
                      : syncState.stage === 'completed'
                        ? 'Sincronização concluída'
                        : syncState.stage === 'groups'
                          ? 'Sincronizando grupos…'
                          : 'Sincronizando contatos…'
                    : '');
                if (!label && !running && !isThisSectorSync) return null;
                return (
                  <div className="sync-status-message" style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      {running ? <FiLoader className="spin" /> : null}
                      <span style={{ fontSize: 13 }}>
                        {label}
                        {progress.total > 0
                          ? ` (${progress.current}/${progress.total})`
                          : ''}
                      </span>
                    </div>
                    {(running || isThisSectorSync) && (
                      <div
                        style={{
                          height: 6,
                          borderRadius: 4,
                          background: 'var(--border-color)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: '100%',
                            borderRadius: 4,
                            background:
                              syncState.stage === 'error'
                                ? 'var(--error-color)'
                                : syncState.stage === 'completed'
                                  ? 'var(--success-color)'
                                  : 'var(--primary-color, #22c55e)',
                            transition: 'transform 0.35s ease',
                            transform: `scaleX(${pct / 100})`,
                            transformOrigin: 'left center',
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Email Connection */}
          {hasModule('email') && (
          <div className="sector-channel">
            {(() => {
              const es = emailStatus[sector.id!];
              const isConnected = es?.status === 'connected';
              const provider = es?.provider || 'email';
              const isOAuthProvider = provider === 'gmail' || provider === 'microsoft';
              const emailStatusLabel = !isConnected
                ? 'Desconectado'
                : provider === 'gmail'
                  ? 'Gmail'
                  : provider === 'microsoft'
                    ? 'Microsoft'
                    : 'Salvo';
              const emailStatusColor = !isConnected ? 'var(--error-color)' : isOAuthProvider ? 'var(--success-color)' : 'var(--text-tertiary)';
              return (
                <>
                  <div className="sector-channel-head">
                    <h4><FiMail size={16} /> E-mail</h4>
                    <div className="whatsapp-status" style={{ color: emailStatusColor }}>
                      <span className="status-dot" style={{ backgroundColor: emailStatusColor }} />
                      <span>{emailStatusLabel}</span>
                    </div>
                  </div>
                  {!isConnected && es?.lastError ? (
                    <div className="email-connection-error-banner" role="alert">
                      {es.lastError}
                    </div>
                  ) : null}
                  <div className="whatsapp-buttons-container">
                    {!isConnected ? (
                      <button
                        className="whatsapp-connect-button"
                        onClick={() => {
                          setSmtpSectorId(sector.id!);
                          setSmtpModalScreen('providers');
                          setShowSmtpModal(true);
                        }}
                      >
                        Conectar e-mail
                      </button>
                    ) : (
                      <button
                        className="whatsapp-connect-button is-danger"
                        onClick={() => setConfirmDisconnectSectorId(sector.id!)}
                        disabled={disconnectingSectorId === sector.id}
                      >
                        {disconnectingSectorId === sector.id ? 'Desconectando…' : 'Desconectar'}
                      </button>
                    )}
                    {isConnected && sector.id != null ? (
                      <button
                        type="button"
                        className="whatsapp-connect-button is-quiet"
                        onClick={() => {
                          const sid = sector.id!;
                          setDeliverabilityModalSectorId(sid);
                          void fetchDeliverability(sid);
                        }}
                      >
                        Entregabilidade
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>
          )}

          {hasModule('calendar') && (
          <div className="sector-channel">
            {(() => {
              const cs = calendarStatus[sector.id!];
              const isConnected = cs?.status === 'connected';
              const needsReconnect = cs?.needsReconnect === true;
              const statusColor = isConnected ? 'var(--success-color)' : 'var(--error-color)';
              const statusLabel = isConnected ? (cs?.googleEmail || 'Conectada') : needsReconnect ? 'Precisa reconectar' : 'Desconectada';
              const choices = calendarChoices[sector.id!] ?? [];
              return (
                <>
                  <div className="sector-channel-head">
                    <h4><FiCalendar size={16} /> Agenda</h4>
                    <div className="whatsapp-status" style={{ color: statusColor }}>
                      <span className="status-dot" style={{ backgroundColor: statusColor }} />
                      <span>{statusLabel}</span>
                    </div>
                  </div>
                  {cs?.calendarSummary ? (
                    <p className="sector-description">{cs.calendarSummary}</p>
                  ) : null}
                  {!isConnected && cs?.lastError ? (
                    <div className="email-connection-error-banner" role="alert">
                      {cs.lastError}
                    </div>
                  ) : null}
                  <div className="whatsapp-buttons-container">
                    {!isConnected ? (
                      <button
                        className="whatsapp-connect-button"
                        disabled={connectingCalendarId === sector.id}
                        onClick={() => {
                          const sid = sector.id!;
                          setConnectingCalendarId(sid);
                          void fetchCalendarAuthorizeUrlAndRedirect(sid).catch(() => {
                            addToast('Não foi possível abrir o Google Agenda.', 'error');
                            setConnectingCalendarId(null);
                          });
                        }}
                      >
                        {connectingCalendarId === sector.id ? 'Conectando…' : needsReconnect ? 'Reconectar' : 'Conectar'}
                      </button>
                    ) : (
                      <>
                        <button
                          className="whatsapp-connect-button is-danger"
                          onClick={() => setConfirmDisconnectCalendarId(sector.id!)}
                          disabled={disconnectingCalendarId === sector.id}
                        >
                          {disconnectingCalendarId === sector.id ? 'Desconectando…' : 'Desconectar'}
                        </button>
                        <button
                          type="button"
                          className="whatsapp-connect-button is-quiet"
                          onClick={() => {
                            const sid = sector.id!;
                            void listGoogleCalendars(sid)
                              .then(list => setCalendarChoices(prev => ({ ...prev, [sid]: list })))
                              .catch(() => addToast('Não foi possível listar os calendários.', 'error'));
                          }}
                        >
                          Trocar
                        </button>
                      </>
                    )}
                  </div>
                  {choices.length > 0 ? (
                    <select
                      className="sector-channel-select"
                      aria-label="Calendário"
                      value={cs?.calendarId ?? ''}
                      onChange={(e) => {
                        const sid = sector.id!;
                        void selectGoogleCalendar(sid, e.target.value)
                          .then(status => {
                            setCalendarStatus(prev => ({ ...prev, [sid]: status }));
                            addToast('Calendário atualizado.', 'success');
                          })
                          .catch(() => addToast('Não foi possível trocar o calendário.', 'error'));
                      }}
                    >
                      {choices.map(choice => (
                        <option key={choice.id} value={choice.id}>{choice.summary || choice.id}</option>
                      ))}
                    </select>
                  ) : null}
                </>
              );
            })()}
          </div>
          )}

          <WhatsAppCloudCard
            sectorId={sector.id!}
            status={metaStatus[sector.id!]}
            hasSpark={sector.sparkIsActive !== undefined && sector.sparkIsActive !== null}
            disconnecting={disconnectingMetaId === sector.id}
            onConnected={result => handleWhatsAppConnected(sector.id!, result)}
            onDisconnect={() => setConfirmDisconnectMeta({ sectorId: sector.id!, channel: 'whatsapp' })}
            onError={message => addToast(message, 'error')}
          />

          <WebchatSiteCard sectorId={sector.id!} onError={message => addToast(message, 'error')} />

          <MetaChannelsCard
            status={metaStatus[sector.id!]}
            connecting={connectingMeta && connectingMeta.sectorId === sector.id ? connectingMeta.channel : null}
            disconnecting={disconnectingMetaId === sector.id}
            onChoosePages={() => setMetaPickerSectorId(sector.id!)}
            onDisconnect={channel => setConfirmDisconnectMeta({ sectorId: sector.id!, channel })}
            onConnect={channel => {
              const sid = sector.id!;
              setConnectingMeta({ sectorId: sid, channel });
              void fetchMetaAuthorizeUrlAndRedirect(sid, channel).catch((e: unknown) => {
                const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                addToast(msg?.trim() || 'Não foi possível abrir o login da Meta.', 'error');
                setConnectingMeta(null);
              });
            }}
          />
        </div>
      </div>
    );
  };

  const sparkMinutesRemaining = sparkPending ? sparkMinutesLeft(sparkPending.expiresAt) : 0;

  return (
    <div className="sectors-page-wrapper">
      <div className="sectors-screen">
        <div className="sectors-header">
          <div className="header-content">
            <h1>Conexões</h1>
          </div>
          <div className="sectors-header-actions">
            <button
              type="button"
              className="header-button-secondary"
              onClick={() => navigate('/google-ads-leads')}
            >
              Google Ads
            </button>
            {sparkAvailable && !Object.values(metaStatus).some(isWhatsAppCloudConfigured) && (
              <button
                type="button"
                className="connect-whatsapp-button"
                onClick={() => openSparkConnect()}
              >
                Conectar WhatsApp Business
              </button>
            )}
            <button
              className="add-button"
              onClick={handleOpenDrawer}
            >
              <FiPlus />
              Nova conexão
            </button>
          </div>
        </div>

        {sparkChecking && (
          <div className="spark-connect-banner" role="status">
            Verificando a conexão…
          </div>
        )}
        {!sparkChecking && sparkFailure && (
          <div className="spark-connect-banner spark-connect-banner-error" role="alert">
            <p>{sparkFailure.userMessage}</p>
            <button
              type="button"
              className="connect-whatsapp-button"
              onClick={() => openSparkConnect(sparkFailure.connectionName, sparkFailure.sectorId ?? undefined)}
            >
              Começar de novo
            </button>
          </div>
        )}
        {!sparkChecking && !sparkFailure && sparkPending?.url && (
          <div className="spark-connect-banner" role="status">
            <p>
              Conectando o WhatsApp. {sparkMinutesRemaining === 1 ? 'Falta' : 'Faltam'} {sparkMinutesRemaining}{' '}
              {sparkMinutesRemaining === 1 ? 'minuto' : 'minutos'}.
            </p>
            <button
              type="button"
              className="connect-whatsapp-button"
              onClick={() => {
                if (sparkPending.url) window.location.assign(sparkPending.url);
              }}
            >
              Continuar
            </button>
          </div>
        )}

        <div className="sectors-content">
          {sectors.length === 0 && !isInitialLoading ? (
            <div className="empty-state">
              <h3>Nenhuma conexão ainda</h3>
              <p>Crie uma para vincular o WhatsApp.</p>
            </div>
          ) : (
            <div className="sectors-grid">
              {sectors.map(sector => renderSectorCard(sector))}
            </div>
          )}
        </div>

        {isInitialLoading && (
          <div className="loading-overlay">
            <LoadingSpinner size="lg" color="primary" />
            <span className="loading-text">Carregando conexões...</span>
          </div>
        )}

        <SharedModal
          open={sparkModalOpen}
          onClose={() => {
            if (sparkStarting) return;
            setSparkModalOpen(false);
          }}
          title="Conectar WhatsApp Business"
          size="sm"
          closeOnOverlayClick={!sparkStarting}
          footer={
            <>
              <button
                type="button"
                className="shared-modal__btn shared-modal__btn--secondary"
                onClick={() => setSparkModalOpen(false)}
                disabled={sparkStarting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="spark-connect-form"
                className="shared-modal__btn shared-modal__btn--primary"
                disabled={sparkStarting}
              >
                {sparkStarting ? 'Abrindo a Meta…' : 'Continuar na Meta'}
              </button>
            </>
          }
        >
          <form
            id="spark-connect-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleStartSparkConnect();
            }}
          >
            <p className="shared-modal__text spark-connect-note">
              Você vai entrar com a Meta e escolher o número do WhatsApp. Este link vale por 20 minutos. Depois, trazemos você de volta para cá.
            </p>
            <div className="shared-modal__form-group">
              <label htmlFor="spark-connection-name">Nome da conexão</label>
              <input
                id="spark-connection-name"
                type="text"
                className={sparkNameError ? 'shared-modal__input--error' : undefined}
                value={sparkModalName}
                placeholder="Ex.: WhatsApp Vendas"
                autoComplete="off"
                onChange={(event) => {
                  setSparkModalName(event.target.value);
                  if (sparkNameError) setSparkNameError('');
                }}
              />
              {sparkNameError && <span className="shared-modal__error">{sparkNameError}</span>}
            </div>
          </form>
        </SharedModal>

        <ConfirmModal
          open={confirmDeleteId !== null}
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={async () => {
            if (confirmDeleteId == null) return;
            await handleDelete(confirmDeleteId);
          }}
          title="Confirmar exclusão"
          message={confirmDeleteId ? `Tem certeza de que quer excluir a conexão "${sectors.find(s => s.id === confirmDeleteId)?.name || ''}"? Essa ação não pode ser desfeita.` : ''}
          confirmLabel="Confirmar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={confirmDeleteId ? isDeleting[confirmDeleteId] : false}
        />

        <ConfirmModal
          open={confirmDisconnectSectorId !== null}
          onClose={() => setConfirmDisconnectSectorId(null)}
          onConfirm={async () => {
            const sid = confirmDisconnectSectorId;
            if (!sid) return;
            setDisconnectingSectorId(sid);
            try {
              await disconnectEmail(sid);
              const status = await getEmailConnectionStatus(sid);
              setEmailStatus(prev => ({ ...prev, [sid]: status }));
              addToast('E-mail desconectado', 'success');
            } catch (e) {
              addToast('Erro ao desconectar', 'error');
              throw e;
            } finally {
              setDisconnectingSectorId(null);
            }
          }}
          title="Desconectar e-mail"
          message="Tem certeza de que quer desconectar este e-mail? A sincronização vai parar."
          confirmLabel="Desconectar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={disconnectingSectorId !== null}
        />

        <ConfirmModal
          open={confirmDisconnectCalendarId !== null}
          onClose={() => setConfirmDisconnectCalendarId(null)}
          onConfirm={async () => {
            const sid = confirmDisconnectCalendarId;
            if (!sid) return;
            setDisconnectingCalendarId(sid);
            try {
              const status = await disconnectCalendar(sid);
              setCalendarStatus(prev => ({ ...prev, [sid]: status }));
              setCalendarChoices(prev => ({ ...prev, [sid]: [] }));
              addToast('Google Agenda desconectada.', 'success');
            } catch (e) {
              addToast('Erro ao desconectar a agenda.', 'error');
              throw e;
            } finally {
              setDisconnectingCalendarId(null);
            }
          }}
          title="Desconectar Google Agenda"
          message="A agenda local continua funcionando. Eventos do Google deixam de bloquear horários."
          confirmLabel="Desconectar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={disconnectingCalendarId !== null}
        />

        <ConfirmModal
          open={confirmDisconnectMeta !== null}
          onClose={() => setConfirmDisconnectMeta(null)}
          onConfirm={async () => {
            if (!confirmDisconnectMeta) return;
            const { sectorId: sid, channel } = confirmDisconnectMeta;
            setDisconnectingMetaId(sid);
            try {
              const status = await disconnectMeta(sid, channel);
              setMetaStatus(prev => ({ ...prev, [sid]: status }));
              if (channel === 'whatsapp') {
                addToast('WhatsApp oficial desconectado.', 'success');
                // O setor volta ao estado anterior (is_official muda no servidor).
                void fetchSectors(true);
                notifySectorsUpdated();
              } else {
                addToast(`${channel === 'instagram' ? 'Instagram' : 'Messenger'} desconectado.`, 'success');
              }
            } catch (e) {
              addToast('Erro ao desconectar da Meta.', 'error');
              throw e;
            } finally {
              setDisconnectingMetaId(null);
            }
          }}
          title={
            confirmDisconnectMeta?.channel === 'whatsapp'
              ? 'Desconectar WhatsApp oficial'
              : confirmDisconnectMeta?.channel === 'instagram' ? 'Desconectar Instagram' : 'Desconectar Messenger'
          }
          message={`As conversas continuam no Mends, mas este setor deixa de receber e responder mensagens pelo ${
            confirmDisconnectMeta?.channel === 'whatsapp'
              ? 'WhatsApp oficial'
              : confirmDisconnectMeta?.channel === 'instagram' ? 'Instagram' : 'Messenger'
          }.`}
          confirmLabel="Desconectar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={disconnectingMetaId !== null}
        />

        {metaPickerSectorId !== null ? (
          <MetaAccountPicker
            open
            sectorId={metaPickerSectorId}
            onClose={closeMetaPicker}
            onSaved={(status, errors) => {
              const sid = metaPickerSectorId;
              setMetaStatus(prev => ({ ...prev, [sid]: status }));
              closeMetaPicker();
              errors.forEach(err => addToast(err.message, 'error'));
              if (status.accounts.some(a => a.channel === 'messenger') && errors.length === 0) addToast('Messenger conectado.', 'success');
            }}
          />
        ) : null}

        <ConfirmModal
          open={confirmWhatsAppRelinkSectorId !== null}
          onClose={() => setConfirmWhatsAppRelinkSectorId(null)}
          onConfirm={() => {
            const sid = confirmWhatsAppRelinkSectorId;
            if (!sid) return;
            proceedShowQRCode(sid, true);
          }}
          title="Reconectar o WhatsApp?"
          message={
            <>
              <p style={{ margin: '0 0 12px' }}>
                Isso encerra a sessão atual do WhatsApp nesta conexão. O envio e o recebimento de mensagens param
                até você vincular o aparelho de novo.
              </p>
              <p style={{ margin: 0 }}>
                Você vai precisar escanear um novo QR code com o celular. As conversas já salvas no sistema não são
                apagadas.
              </p>
            </>
          }
          confirmLabel="Continuar"
          cancelLabel="Cancelar"
          variant="warning"
        />

        <SharedModal
          open={showQRCode}
          onClose={handleCloseQRCode}
          title="Conectar WhatsApp"
          size="sm"
          className="qrcode-connect-modal"
        >
          <p className="qrcode-modal-subtitle">
            Escaneie o código com o celular para vincular esta conexão.
          </p>

          {whatsappStatus?.status === 'connected' ? (
            <div className="qrcode-success">
              <FiCheckCircle size={48} />
              <p>WhatsApp conectado!</p>
            </div>
          ) : qrCodeData?.qrcode?.trim() ? (
            <div className="qrcode-wrapper">
              <div className="qrcode-container">
                <img
                  src={formatWhatsAppQrSrc(qrCodeData.qrcode)}
                  alt="QR code para conectar o WhatsApp"
                />
              </div>
              <button
                type="button"
                className="qrcode-refresh-btn"
                onClick={() => selectedSectorForQR && void fetchQRCode(selectedSectorForQR, false)}
              >
                <FiRefreshCw size={14} />
                Atualizar QR
              </button>
            </div>
          ) : (
            <div className="qrcode-loading">
              <div
                className="qrcode-skeleton"
                role="status"
                aria-label={isLoadingQRCode ? 'Gerando o QR code' : 'Aguardando o QR code'}
              >
                <div className="qrcode-skeleton-shimmer" aria-hidden />
                <div className="qrcode-skeleton-grid" aria-hidden />
                <div className="qrcode-skeleton-scan" aria-hidden />
                <div className="qrcode-skeleton-corners" aria-hidden>
                  <span /><span /><span /><span />
                </div>
                <FiSmartphone className="qrcode-skeleton-icon" size={28} aria-hidden />
              </div>
              {!isLoadingQRCode && (
                <div className="qrcode-loading-actions">
                  {qrError ? <p className="qrcode-loading-hint">{qrError}</p> : null}
                  <button
                    type="button"
                    className="qrcode-refresh-btn"
                    onClick={() => selectedSectorForQR && void fetchQRCode(selectedSectorForQR, false)}
                  >
                    <FiRefreshCw size={14} />
                    Tentar de novo
                  </button>
                </div>
              )}
            </div>
          )}
        </SharedModal>

        <SharedModal
          open={deliverabilityModalSectorId !== null}
          onClose={() => setDeliverabilityModalSectorId(null)}
          title="Domínio e entregabilidade (SPF / DKIM / DMARC)"
          size="lg"
          footer={
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setDeliverabilityModalSectorId(null)}>
                Fechar
              </button>
              {deliverabilityModalSectorId != null && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!!deliveryLoading[deliverabilityModalSectorId]}
                  onClick={() => void fetchDeliverability(deliverabilityModalSectorId)}
                >
                  Verificar de novo
                </button>
              )}
            </div>
          }
        >
          {deliverabilityModalSectorId != null && (
            <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              {deliveryLoading[deliverabilityModalSectorId] && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiLoader className="spin" />
                  Consultando o DNS público (visão global)…
                </div>
              )}
              {!deliveryLoading[deliverabilityModalSectorId] && deliveryFetchError[deliverabilityModalSectorId] && (
                <p style={{ color: 'var(--error-color)', margin: 0 }}>{deliveryFetchError[deliverabilityModalSectorId]}</p>
              )}
              {!deliveryLoading[deliverabilityModalSectorId] && deliveryCheck[deliverabilityModalSectorId] && (() => {
                const d = deliveryCheck[deliverabilityModalSectorId]!;
                const copyLine = (label: string, text: string) => (
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(text).then(
                        () => addToast(`${label} copiado`, 'success'),
                        () => addToast('Não foi possível copiar', 'error')
                      );
                    }}
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      padding: '6px 10px',
                      minHeight: 36,
                      borderRadius: 4,
                      border: '1px solid var(--border-strong)',
                      background: 'var(--bg-card)',
                      cursor: 'pointer',
                    }}
                  >
                    Copiar
                  </button>
                );
                const row = (ok: boolean, title: string, body: React.ReactNode) => (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      marginBottom: 10,
                      paddingBottom: 10,
                      borderBottom: '1px solid var(--border-color)',
                    }}
                  >
                    <span style={{ marginTop: 2, flexShrink: 0 }}>
                      {ok ? (
                        <FiCheckCircle style={{ color: 'var(--success-color)' }} size={18} />
                      ) : (
                        <FiX style={{ color: 'var(--error-color)' }} size={18} />
                      )}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong>{title}</strong>
                      <div style={{ color: 'var(--text-tertiary)', marginTop: 4 }}>{body}</div>
                    </div>
                  </div>
                );
                if (d.error) {
                  return <p style={{ color: 'var(--error-color)', margin: 0 }}>{d.error}</p>;
                }
                if (d.isConsumerMailbox) {
                  return (
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                      {d.consumerMailboxNote}
                    </p>
                  );
                }
                const sortRec = (d.recommendedRecords ?? []).slice().sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
                return (
                  <>
                    {d.alignmentGuide && (
                      <div
                        style={{
                          marginBottom: 14,
                          padding: 12,
                          background: 'var(--bg-elevated)',
                          borderRadius: 8,
                          border: '1px solid var(--border-color)',
                          fontSize: 13,
                          color: 'var(--text-secondary)',
                          lineHeight: 1.55,
                        }}
                      >
                        <strong style={{ color: 'var(--text-primary)' }}>SPF + DKIM + DMARC (mesmo modelo)</strong>
                        <p style={{ margin: '8px 0 0' }}>{d.alignmentGuide}</p>
                      </div>
                    )}
                    {d.mendsDkim?.hasGeneratedKeys &&
                      d.mendsDkim.dnsTxtValue &&
                      !d.dkim.selectorsChecked.some(
                        (s) => s.selector === d.mendsDkim?.selector && s.found
                      ) && (
                      <div
                        style={{
                          marginBottom: 14,
                          padding: 12,
                          background: 'var(--primary-soft)',
                          borderRadius: 8,
                          border: '1px solid var(--primary-soft-strong)',
                        }}
                      >
                        <strong style={{ color: 'var(--primary-light)' }}>DKIM (Mends, nesta conexão)</strong>
                        <p style={{ margin: '8px 0 6px', fontSize: 12, color: 'var(--primary-light)' }}>
                          Depois que você salva o SMTP, o Mends gera uma chave para <strong>{d.mendsDkim.domain}</strong>. Adicione
                          este TXT no seu DNS (Hostinger, HostGator, Cloudflare etc.):
                        </p>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                          <span style={{ color: 'var(--text-tertiary)' }}>Host / nome (geralmente um destes):</span>{' '}
                          <code style={{ wordBreak: 'break-all' }}>{d.mendsDkim.selector}._domainkey</code>
                          {' · '}
                          <span style={{ color: 'var(--text-tertiary)' }}>ou completo:</span>{' '}
                          <code style={{ wordBreak: 'break-all' }}>{d.mendsDkim.dnsHostFqdn}</code>
                          {copyLine('Host', d.mendsDkim.dnsHostFqdn ?? '')}
                        </div>
                        <pre
                          style={{
                            margin: '8px 0 0',
                            padding: 8,
                            background: 'var(--bg-card)',
                            borderRadius: 6,
                            fontSize: 11,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {d.mendsDkim.dnsTxtValue}
                        </pre>
                        {copyLine('Valor TXT do DKIM', d.mendsDkim.dnsTxtValue)}
                        {d.mendsDkim.providerNote && (
                          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>{d.mendsDkim.providerNote}</p>
                        )}
                      </div>
                    )}
                    <div
                      style={{
                        marginBottom: 12,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: 999,
                          background: d.overallHealthy ? 'var(--success-bg)' : 'var(--warning-bg)',
                          color: d.overallHealthy ? 'var(--success-color)' : 'var(--warning-color)',
                          fontWeight: 600,
                          fontSize: 12,
                        }}
                      >
                        {d.overallHealthy ? 'DNS em ordem' : 'DNS precisa de atenção'}
                      </span>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
                        Conta: <strong>{d.accountEmail}</strong>
                        {' · '}
                        Domínio: <strong>{d.domain}</strong>
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12, width: '100%' }}>
                        Verificado em: {new Date(d.checkedAtUtc).toLocaleString(undefined, { timeZone: 'UTC' })} UTC
                      </span>
                    </div>
                    {row(d.spf.found, 'SPF', <>
                      {d.spf.summary}
                      {d.spf.spfTxtValues?.length ? (
                        <pre
                          style={{
                            margin: '8px 0 0',
                            padding: 8,
                            background: 'var(--bg-elevated)',
                            borderRadius: 6,
                            fontSize: 11,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {d.spf.spfTxtValues.join('\n')}
                        </pre>
                      ) : null}
                    </>)}
                    {row(d.dmarc.found, 'DMARC', <>
                      {d.dmarc.summary}
                      {d.dmarc.rawRecord ? (
                        <pre
                          style={{
                            margin: '8px 0 0',
                            padding: 8,
                            background: 'var(--bg-elevated)',
                            borderRadius: 6,
                            fontSize: 11,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {d.dmarc.rawRecord}
                        </pre>
                      ) : null}
                    </>)}
                    {row(
                      d.dkim.anyFound,
                      'DKIM',
                      <>
                        {d.dkim.anyFound
                          ? 'Chave DKIM encontrada em pelo menos um seletor comum para o seu tipo de conexão.'
                          : 'Nenhum DKIM encontrado nos seletores que testamos (seu provedor pode usar outro nome; confira no painel do Google / Microsoft).'}
                        <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                          {d.dkim.selectorsChecked.map(s => (
                            <li key={s.selector}>
                              <code>{s.selector}</code>: {s.found ? 'ok' : '—'}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {sortRec.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <strong>Registros DNS sugeridos (mesma ordem: SPF → DKIM → DMARC)</strong>
                        <p style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: '6px 0 10px' }}>
                          Copie nesta ordem para manter o alinhamento. A propagação pode levar até 48 horas; use Verificar de novo para conferir.
                        </p>
                        {sortRec.map((rec, idx) => (
                          <div
                            key={idx}
                            style={{
                              marginBottom: 10,
                              padding: 10,
                              background: 'var(--bg-card)',
                              borderRadius: 6,
                              border: '1px solid var(--border-color)',
                            }}
                          >
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  color: 'var(--text-tertiary)',
                                  background: 'var(--bg-tertiary)',
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                }}
                              >
                                Passo {rec.step ?? idx + 1} · {rec.role || 'registro'}
                              </span>
                              <span style={{ fontWeight: 600 }}>{rec.kind}</span>
                              <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{rec.host}</code>
                              {copyLine('Registro', `${rec.kind} ${rec.host} → ${rec.value}`)}
                            </div>
                            <pre
                              style={{
                                margin: '8px 0 0',
                                padding: 8,
                                background: 'var(--bg-tertiary)',
                                borderRadius: 4,
                                fontSize: 11,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowX: 'auto',
                              }}
                            >
                              {rec.value}
                            </pre>
                            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>{rec.note}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </SharedModal>

        {/* Drawer for creating/editing sector */}
        {isDrawerOpen && (
          <>
            <div className="drawer-overlay" onClick={handleCloseDrawer} />
            <div className="sector-drawer">
              <div className="drawer-header">
                <h2>{editingSector ? 'Editar conexão' : 'Nova conexão'}</h2>
                <button className="close-button" onClick={handleCloseDrawer}>
                  <SectorIconX />
                </button>
              </div>
              <div className="drawer-content">
                {renderForm()}
              </div>
            </div>
          </>
        )}

        {/* Email: OAuth (Gmail, Microsoft) or SMTP+IMAP password (Hostinger, HostGator). */}
        {showSmtpModal && smtpSectorId && (
          <div className="smtp-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="smtp-modal-title">
            <div
              className={`smtp-modal smtp-modal--wizard smtp-modal--step1 ${
                smtpModalScreen === 'providers' ? 'smtp-modal--oauth-only' : 'smtp-modal--hostinger-form'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="smtp-modal-header">
                <div className="smtp-modal-title-wrap">
                  <FiMail className="smtp-modal-icon" size={22} />
                  <h3 id="smtp-modal-title">
                    {smtpModalScreen === 'hostinger'
                      ? 'E-mail Hostinger'
                      : smtpModalScreen === 'hostgator'
                        ? 'E-mail HostGator'
                        : 'Conectar e-mail'}
                  </h3>
                </div>
                <button
                  type="button"
                  className="smtp-modal-close"
                  onClick={() => handleCloseSmtpModal()}
                  aria-label="Fechar"
                >
                  <SectorIconX size={20} />
                </button>
              </div>
              <div className="smtp-modal-body">
                {smtpModalScreen === 'providers' ? (
                  <>
                    <div className="smtp-security-banner">
                      <SectorIconCheckCircle size={18} />
                      <span>
                        O OAuth usa Google ou Microsoft. Hostinger / HostGator usam a senha do seu e-mail; as credenciais são criptografadas ao salvar.
                      </span>
                    </div>
                    <div className="smtp-wizard-step">
                      <p className="smtp-modal-hint">
                        Escolha <strong>Gmail</strong>, <strong>Outlook</strong>, <strong>Hostinger</strong> ou <strong>HostGator</strong> (SMTP / IMAP).
                      </p>
                      <div className="smtp-provider-grid smtp-provider-grid--four">
                        <button
                          type="button"
                          className="smtp-provider-card smtp-provider-card--gmail"
                          onClick={async () => {
                            if (!smtpSectorId) return;
                            try {
                              await fetchOAuthAuthorizeUrlAndRedirect(smtpSectorId);
                              handleCloseSmtpModal();
                            } catch (e: unknown) {
                              const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                              addToast(msg || 'Não foi possível iniciar a conexão com o Gmail. Tente de novo.', 'error');
                            }
                          }}
                        >
                          <span className="smtp-provider-name">Gmail</span>
                          <span className="smtp-provider-host smtp-provider-host--muted">Entrar com Google</span>
                        </button>
                        <button
                          type="button"
                          className="smtp-provider-card smtp-provider-card--microsoft"
                          onClick={async () => {
                            if (!smtpSectorId) return;
                            try {
                              await fetchMicrosoftOAuthAuthorizeUrlAndRedirect(smtpSectorId);
                              handleCloseSmtpModal();
                            } catch (e: unknown) {
                              const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                              addToast(msg || 'Não foi possível iniciar a conexão com o Outlook / Microsoft 365. Tente de novo.', 'error');
                            }
                          }}
                        >
                          <span className="smtp-provider-name">Outlook</span>
                          <span className="smtp-provider-host smtp-provider-host--muted">Entrar com Microsoft</span>
                        </button>
                        <button
                          type="button"
                          className="smtp-provider-card smtp-provider-card--hostinger"
                          onClick={() => {
                            setSmtpManualHost('smtp.hostinger.com');
                            setSmtpManualPort(587);
                            setSmtpModalScreen('hostinger');
                          }}
                        >
                          <span className="smtp-provider-name">Hostinger</span>
                          <span className="smtp-provider-host smtp-provider-host--muted">SMTP e IMAP (e-mail + senha)</span>
                        </button>
                        <button
                          type="button"
                          className="smtp-provider-card smtp-provider-card--hostgator"
                          onClick={() => {
                            setSmtpManualHost('');
                            setSmtpManualPort(465);
                            setSmtpModalScreen('hostgator');
                          }}
                        >
                          <span className="smtp-provider-name">HostGator</span>
                          <span className="smtp-provider-host smtp-provider-host--muted">SMTP e IMAP (e-mail + senha)</span>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="smtp-wizard-step">
                    {smtpModalScreen === 'hostinger' ? (
                      <p className="smtp-modal-hint">
                        Use o endereço completo da caixa de e-mail e a senha. Padrão: <code>smtp.hostinger.com</code>, porta <strong>587</strong> (TLS). O IMAP usa{' '}
                        <code>imap.hostinger.com</code> automaticamente.
                      </p>
                    ) : (
                      <p className="smtp-modal-hint">
                        Use o <strong>nome do servidor de e-mail</strong> do cPanel da HostGator (Contas de e-mail → Conectar dispositivos), por exemplo{' '}
                        <code>gator####.hostgator.com</code> ou <code>mail.seudominio.com</code>. Porta padrão <strong>465</strong> (SSL). O IMAP usa o mesmo host
                        na porta 993, a menos que você use <code>smtp.hostgator.com</code> (aí o IMAP é <code>imap.hostgator.com</code>).
                      </p>
                    )}
                    <div className="smtp-form">
                      <div className="smtp-form-row smtp-form-row--host-port">
                        <div className="smtp-form-group smtp-form-group--host">
                          <label htmlFor="smtp-manual-host">Host SMTP</label>
                          <input
                            id="smtp-manual-host"
                            className="smtp-input"
                            value={smtpManualHost}
                            onChange={e => setSmtpManualHost(e.target.value.trim())}
                            placeholder={smtpModalScreen === 'hostgator' ? 'gator####.hostgator.com ou mail.seudominio.com' : undefined}
                            autoComplete="off"
                          />
                        </div>
                        <div className="smtp-form-group smtp-form-group--port">
                          <label htmlFor="smtp-manual-port">Porta</label>
                          <input
                            id="smtp-manual-port"
                            type="number"
                            className="smtp-input"
                            value={smtpManualPort}
                            onChange={e =>
                              setSmtpManualPort(
                                parseInt(e.target.value, 10) || (smtpModalScreen === 'hostgator' ? 465 : 587)
                              )
                            }
                            min={1}
                            max={65535}
                          />
                        </div>
                      </div>
                      <div className="smtp-form-group">
                        <label htmlFor="smtp-manual-email">Endereço de e-mail</label>
                        <input
                          id="smtp-manual-email"
                          type="email"
                          className="smtp-input"
                          value={smtpManualEmail}
                          onChange={e => setSmtpManualEmail(e.target.value.trim())}
                          placeholder="voce@seudominio.com"
                          autoComplete="username"
                        />
                      </div>
                      <div className="smtp-form-group">
                        <label htmlFor="smtp-manual-password">Senha</label>
                        <input
                          id="smtp-manual-password"
                          type="password"
                          className="smtp-input"
                          value={smtpManualPassword}
                          onChange={e => setSmtpManualPassword(e.target.value)}
                          autoComplete="current-password"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="smtp-modal-footer smtp-wizard-nav">
                {smtpModalScreen === 'hostinger' || smtpModalScreen === 'hostgator' ? (
                  <>
                    <button type="button" className="smtp-btn smtp-btn-cancel" onClick={() => setSmtpModalScreen('providers')}>
                      Voltar
                    </button>
                    <div className="smtp-wizard-nav-right" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="smtp-btn smtp-btn-secondary"
                        disabled={smtpManualBusy || !smtpManualEmail || !smtpManualPassword}
                        onClick={async () => {
                          if (!smtpSectorId) return;
                          setSmtpManualBusy(true);
                          try {
                            const r = await testSmtpConnection({
                              sectorId: smtpSectorId,
                              host: smtpManualHost,
                              port: smtpManualPort,
                              username: smtpManualEmail,
                              password: smtpManualPassword,
                            });
                            if (r.smtpOk && r.imapOk) addToast('Conexão SMTP e IMAP funcionando.', 'success');
                            else
                              addToast(
                                [r.smtpOk ? '' : `SMTP: ${r.smtpError || 'falhou'}`, r.imapOk ? '' : `IMAP: ${r.imapError || 'falhou'}`]
                                  .filter(Boolean)
                                  .join(' '),
                                'error'
                              );
                          } catch (e: unknown) {
                            const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                            addToast(msg || 'O teste de conexão falhou.', 'error');
                          } finally {
                            setSmtpManualBusy(false);
                          }
                        }}
                      >
                        Testar conexão
                      </button>
                      <button
                        type="button"
                        className="smtp-btn smtp-btn-save"
                        disabled={smtpManualBusy || !smtpManualEmail || !smtpManualPassword || !smtpManualHost.trim()}
                        onClick={async () => {
                          if (!smtpSectorId) return;
                          setSmtpManualBusy(true);
                          try {
                            await saveSmtpConfig({
                              sectorId: smtpSectorId,
                              host: smtpManualHost.trim(),
                              port: smtpManualPort,
                              username: smtpManualEmail,
                              password: smtpManualPassword,
                            });
                            addToast(
                              smtpModalScreen === 'hostgator' ? 'E-mail HostGator conectado.' : 'E-mail Hostinger conectado.',
                              'success'
                            );
                            const st = await getEmailConnectionStatus(smtpSectorId);
                            setEmailStatus(prev => ({ ...prev, [smtpSectorId]: st }));
                            handleCloseSmtpModal();
                          } catch (e: unknown) {
                            const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
                            addToast(msg || 'Não foi possível salvar as configurações de e-mail.', 'error');
                          } finally {
                            setSmtpManualBusy(false);
                          }
                        }}
                      >
                        {smtpManualBusy ? 'Salvando…' : 'Salvar e conectar'}
                      </button>
                    </div>
                  </>
                ) : (
                  <button type="button" className="smtp-btn smtp-btn-cancel" onClick={() => handleCloseSmtpModal()}>
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {templateSectorId != null && (
          <SparkMessageTemplatesPanel sectorId={templateSectorId} onClose={() => setTemplateSectorId(null)} />
        )}

        <ToastContainer>
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </ToastContainer>
      </div>
    </div>
  );
};

export default SectorsPage;
